import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Shell from "@/components/Shell";
import { Loading } from "@/components/ui-kit";
import "@/App.css";

import Login from "@/pages/Login";
import TutorDashboard from "@/pages/tutor/TutorDashboard";
import Students from "@/pages/tutor/Students";
import StudentProfile from "@/pages/tutor/StudentProfile";
import Classes from "@/pages/tutor/Classes";
import HomeworkList from "@/pages/tutor/HomeworkList";
import UploadHomework from "@/pages/tutor/UploadHomework";
import HomeworkReview from "@/pages/tutor/HomeworkReview";
import Results from "@/pages/tutor/Results";
import WritingFeedback from "@/pages/tutor/WritingFeedback";
import WritingReview from "@/pages/tutor/WritingReview";
import TutorAdmin from "@/pages/tutor/TutorAdmin";
import Payments from "@/pages/tutor/Payments";
import Communications from "@/pages/tutor/Communications";
import TutorAttemptMark from "@/pages/tutor/TutorAttemptMark";
import TutorCalendar from "@/pages/tutor/TutorCalendar";
import TutorCourse, { TutorCourses } from "@/pages/tutor/TutorCourse";
import StudentCourse from "@/pages/student/StudentCourse";
import StudentCalendar from "@/pages/student/StudentCalendar";
import Leaderboard from "@/pages/Leaderboard";

import StudentDashboard from "@/pages/student/StudentDashboard";
import StudentHomework from "@/pages/student/StudentHomework";
import ExamPlayer from "@/pages/student/ExamPlayer";
import ResultView from "@/pages/student/ResultView";
import Progress from "@/pages/student/Progress";
import History from "@/pages/student/History";
import MistakeBank from "@/pages/student/MistakeBank";
import MistakePractice from "@/pages/student/MistakePractice";
import WritingIndex from "@/pages/student/WritingIndex";
import WritingTask from "@/pages/student/WritingTask";

function Guard({ role, children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role) return <Navigate to={user.role === "tutor" ? "/tutor" : "/student"} replace />;
  return children;
}

function Home() {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={user.role === "tutor" ? "/tutor" : "/student"} replace />;
}

const Tutor = ({ children }) => <Guard role="tutor"><Shell>{children}</Shell></Guard>;
const Student = ({ children }) => <Guard role="student"><Shell>{children}</Shell></Guard>;
// Exam is full-screen (no shell)
const StudentBare = ({ children }) => <Guard role="student">{children}</Guard>;

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Login />} />

            <Route path="/tutor" element={<Tutor><TutorDashboard /></Tutor>} />
            <Route path="/tutor/students" element={<Tutor><Students /></Tutor>} />
            <Route path="/tutor/students/:id" element={<Tutor><StudentProfile /></Tutor>} />
            <Route path="/tutor/classes" element={<Tutor><Classes /></Tutor>} />
            <Route path="/tutor/homework" element={<Tutor><HomeworkList /></Tutor>} />
            <Route path="/tutor/calendar" element={<Tutor><TutorCalendar /></Tutor>} />
            <Route path="/tutor/courses" element={<Tutor><TutorCourses /></Tutor>} />
            <Route path="/tutor/courses/:program/:section" element={<Tutor><TutorCourse /></Tutor>} />
            <Route path="/tutor/upload" element={<Tutor><UploadHomework /></Tutor>} />
            <Route path="/tutor/homework/:id/review" element={<Tutor><HomeworkReview /></Tutor>} />
            <Route path="/tutor/results" element={<Tutor><Results /></Tutor>} />
            <Route path="/tutor/leaderboard" element={<Tutor><Leaderboard /></Tutor>} />
            <Route path="/tutor/writing" element={<Tutor><WritingFeedback /></Tutor>} />
            <Route path="/tutor/writing/:id" element={<Tutor><WritingReview /></Tutor>} />
            <Route path="/tutor/tutors" element={<Tutor><TutorAdmin /></Tutor>} />
            <Route path="/tutor/payments" element={<Tutor><Payments /></Tutor>} />
            <Route path="/tutor/communications" element={<Tutor><Communications /></Tutor>} />
            <Route path="/tutor/attempts/:id" element={<Tutor><TutorAttemptMark /></Tutor>} />

            <Route path="/student" element={<Student><StudentDashboard /></Student>} />
            <Route path="/student/homework" element={<Student><StudentHomework /></Student>} />
            <Route path="/student/calendar" element={<Student><StudentCalendar /></Student>} />
            <Route path="/student/course/:program/:section" element={<Student><StudentCourse /></Student>} />
            <Route path="/student/exam/:hwId" element={<StudentBare><ExamPlayer /></StudentBare>} />
            <Route path="/student/result/:attemptId" element={<Student><ResultView /></Student>} />
            <Route path="/student/progress" element={<Student><Progress /></Student>} />
            <Route path="/student/history" element={<Student><History /></Student>} />
            <Route path="/student/leaderboard" element={<Student><Leaderboard /></Student>} />
            <Route path="/student/mistakes" element={<Student><MistakeBank /></Student>} />
            <Route path="/student/practice" element={<Student><MistakePractice /></Student>} />
            <Route path="/student/writing" element={<Student><WritingIndex /></Student>} />
            <Route path="/student/writing/:hwId" element={<Student><WritingTask /></Student>} />

            <Route path="*" element={<Home />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
